import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSession } from 'shared/store/modules/auth/hooks';
import { selectAccessTokenExpirationDate } from 'shared/store/modules/auth/selectors';

import { useSelector } from 'react-redux';

export function useSessionTimer() {
  const { hasAccessToken, isRenewing, renewSession } = useSession();

  const accessTokenExpirationDate = useSelector(selectAccessTokenExpirationDate);

  const expirationDate = useMemo(() => {
    return moment(accessTokenExpirationDate).startOf('second');
  }, [accessTokenExpirationDate]);

  const calculateSessionTimeRemaining = useCallback(() => {
    return moment.duration(expirationDate.diff(moment(new Date()).startOf('second'))).asMilliseconds();
  }, [expirationDate]);

  // Use a ref to track if we've already initiated renewal to prevent multiple calls
  const renewalInitiatedRef = useRef(false);

  useEffect(() => {
    const checkAndRenew = () => {
      // Calculate time remaining directly without storing in state
      const timeRemaining = calculateSessionTimeRemaining();
      const shouldRenew = timeRemaining <= 0;

      // Only trigger renewal if needed and not already renewing
      if ((shouldRenew || !hasAccessToken) && !isRenewing && !renewalInitiatedRef.current) {
        renewalInitiatedRef.current = true;
        renewSession();
      }
    };
    // Run an immediate check on mount to avoid a window with an expired/missing token
    checkAndRenew();
    const interval = setInterval(checkAndRenew, 1000);

    return () => clearInterval(interval);
  }, [calculateSessionTimeRemaining, hasAccessToken, isRenewing, renewSession]);

  // Reset the renewal flag when renewal completes
  useEffect(() => {
    if (!isRenewing) {
      renewalInitiatedRef.current = false;
    }
  }, [isRenewing]);
}
